import { Box, Checkbox, FormControlLabel, FormGroup, Stack, Typography } from "@mui/material"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"

import { CompanyUserFormValues } from "../../companyUserFormTypes"

type ApproverUserGroup = Readonly<{
    id: number
    name: string
    description?: string
}>

type ApprovalsTabProps = Readonly<{
    userGroups: ReadonlyArray<ApproverUserGroup>
}>

const useStyles = makeStyles()((theme) => ({
    card: {
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".75rem",
        padding: ".25rem",
    },
    row: {
        alignItems: "center",
        width: "100%",
        margin: 0,
        padding: ".375rem .5rem",
    },
    label: {
        display: "flex",
        flexDirection: "column",
    },
}))

export function ApprovalsTab({ userGroups }: ApprovalsTabProps) {
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<CompanyUserFormValues>()

    const selected = values.approverForUserGroupIds

    const toggle = (id: number) => {
        const next = selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id]
        setFieldValue("approverForUserGroupIds", next)
    }

    return (
        <Stack direction="column" spacing="1rem">
            <LocalizedStrict id="company-user-approvals-description">
                <Typography variant="body2" color="text.secondary">
                    Select the user groups this user approves when their members sign up themselves.
                </Typography>
            </LocalizedStrict>

            <LocalizedStrict id="company-user-approvals-heading">
                <Typography variant="subtitle2">Approve user groups</Typography>
            </LocalizedStrict>

            <FormGroup className={classes.card}>
                {userGroups
                    .filter((group) => group.name.trim().length > 0)
                    .map((group) => (
                        <FormControlLabel
                            key={group.id}
                            className={classes.row}
                            control={
                                <Checkbox checked={selected.includes(group.id)} onChange={() => toggle(group.id)} />
                            }
                            label={
                                <Box className={classes.label}>
                                    <Typography variant="subtitle2">{group.name}</Typography>
                                    {group.description && (
                                        <Typography variant="body2" color="text.secondary">
                                            {group.description}
                                        </Typography>
                                    )}
                                </Box>
                            }
                        />
                    ))}
            </FormGroup>
        </Stack>
    )
}
